import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Box,
  Chip,
  InputAdornment,
  OutlinedInput,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';

const FiltersModal = ({ open, onClose, onApply, currentFilters, availableProducts = [], availableAddons = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedGarage } = useGarage();
  
  // Filter states - updated structure
  const [filters, setFilters] = useState({
    customer_ids: [],
    product_ids: [],
    addon_ids: [],
    payment_status: '', // Single selection
    appointment_status: '' // Single selection
  });
  
  // Modal states
  const [showVehicleCustomerPicker, setShowVehicleCustomerPicker] = useState(false);
  const [showServicesPicker, setShowServicesPicker] = useState(false);
  
  // Search states
  const [vehicleCustomerSearch, setVehicleCustomerSearch] = useState('');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searching, setSearching] = useState(false);
  
  // Refs for pickers
  const vehicleCustomerPickerRef = useRef(null);
  const servicesPickerRef = useRef(null);
  
  // Refs for scrolling inputs to top
  const customerInputRef = useRef(null);
  const servicesInputRef = useRef(null);
  const paymentStatusInputRef = useRef(null);
  const appointmentStatusInputRef = useRef(null);
  
  // Refs for auto-focus search inputs
  const customerSearchInputRef = useRef(null);
  const servicesSearchInputRef = useRef(null);
  
  // Available options (fetched from API)
  const [garageCustomers, setGarageCustomers] = useState([]);
  const [filteredGarageCustomers, setFilteredGarageCustomers] = useState([]);
  
  // Store all selected customers data to persist across searches
  const [selectedCustomersData, setSelectedCustomersData] = useState({});
  
  // Static options for payment and appointment status with color coding
  const paymentStatusOptions = [
    { value: 'Free', label: 'Free', color: 'text.primary' },
    { value: 'Paid', label: 'Paid', color: 'success.main' },
    { value: 'Due', label: 'Due', color: 'warning.main' },
    { value: 'In progress', label: 'In progress', color: 'info.main' },
    { value: 'Payment Failure', label: 'Payment Failure', color: 'error.main' }
  ];
  
  const appointmentStatusOptions = [
    { value: 'Upcoming', label: 'Upcoming', color: 'primary.main' },
    { value: 'Ongoing', label: 'Ongoing', color: 'warning.main' },
    { value: 'No show', label: 'No show', color: 'error.main' },
    { value: 'Completed', label: 'Completed', color: 'success.main' },
    { value: 'Cancelled', label: 'Cancelled', color: 'error.main' }
  ];

  // Helper function to scroll input to top of modal
  const scrollToTop = (elementRef) => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  // Initialize filters from props
  useEffect(() => {
    if (currentFilters) {
      setFilters({
        customer_ids: currentFilters.customer_ids || [],
        product_ids: currentFilters.product_ids || [],
        addon_ids: currentFilters.addon_ids || [],
        // Convert array to string for single selection fields if needed
        payment_status: Array.isArray(currentFilters.payment_status) ? currentFilters.payment_status[0] || '' : currentFilters.payment_status || '',
        appointment_status: Array.isArray(currentFilters.appointment_status) ? currentFilters.appointment_status[0] || '' : currentFilters.appointment_status || ''
      });
    }
  }, [currentFilters, open]);

  // Handle click outside to close pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vehicleCustomerPickerRef.current && !vehicleCustomerPickerRef.current.contains(event.target)) {
        setShowVehicleCustomerPicker(false);
      }
      if (servicesPickerRef.current && !servicesPickerRef.current.contains(event.target)) {
        setShowServicesPicker(false);
      }
    };

    if (showVehicleCustomerPicker || showServicesPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showVehicleCustomerPicker, showServicesPicker]);

  // Search garage customers
  const searchGarageCustomers = async () => {
    if (!vehicleCustomerSearch.trim()) return;
    
    setSearching(true);
    setSearchPerformed(false);
    
    try {
      const response = await apiService.getGarageCustomers(selectedGarage.id, { 
        search: vehicleCustomerSearch.trim() 
      });
      const customers = response.data || [];
      setGarageCustomers(customers);
      setFilteredGarageCustomers(customers);
      setSearchPerformed(true);
    } catch (error) {
      console.error('Error searching garage customers:', error);
      setGarageCustomers([]);
      setFilteredGarageCustomers([]);
      setSearchPerformed(true);
    } finally {
      setSearching(false);
    }
  };

  // Handle customer selection
  const handleVehicleCustomerSelect = (customer) => {
    setFilters(prev => {
      const isSelected = prev.customer_ids.includes(customer.id);
      return {
        ...prev,
        customer_ids: isSelected
          ? prev.customer_ids.filter(id => id !== customer.id)
          : [...prev.customer_ids, customer.id]
      };
    });
    
    // Store customer data for persistent display
    setSelectedCustomersData(prev => ({
      ...prev,
      [customer.id]: customer
    }));
    
    setShowVehicleCustomerPicker(false);
    setVehicleCustomerSearch('');
    setSearchPerformed(false);
    setSearching(false);
  };

  // Handle product and addon selection
  const handleProductToggle = (product) => {
    setFilters(prev => {
      const isSelected = prev.product_ids.includes(product.id);
      return {
        ...prev,
        product_ids: isSelected
          ? prev.product_ids.filter(id => id !== product.id)
          : [...prev.product_ids, product.id]
      };
    });
  };

  const handleAddonToggle = (addon) => {
    setFilters(prev => {
      const isSelected = prev.addon_ids.includes(addon.id);
      return {
        ...prev,
        addon_ids: isSelected
          ? prev.addon_ids.filter(id => id !== addon.id)
          : [...prev.addon_ids, addon.id]
      };
    });
  };

  // Handle payment status change (single selection)
  const handlePaymentStatusChange = (event) => {
    setFilters(prev => ({
      ...prev,
      payment_status: event.target.value
    }));
  };

  // Handle appointment status change (single selection)
  const handleAppointmentStatusChange = (event) => {
    setFilters(prev => ({
      ...prev,
      appointment_status: event.target.value
    }));
  };

  // Apply filters - send directly with proper structure, don't pass empty values
  const handleApply = () => {
    const filtersToApply = {
      customer_ids: filters.customer_ids,
      product_ids: filters.product_ids,
      addon_ids: filters.addon_ids,
      payment_status: filters.payment_status || '',
      appointment_status: filters.appointment_status || '',
      // Include labels for display
      labels: {
        customers: {},
        products: {},
        addons: {}
      }
    };
    
    // Add customer labels for ALL selected customers
    filters.customer_ids.forEach(customerId => {
      // Try to get customer from persistent data first, then from search results
      const customer = selectedCustomersData[customerId] || garageCustomers.find(c => c.id === customerId);
      if (customer) {
        filtersToApply.labels.customers[customerId] = `${customer.vehicle?.registration_number || 'N/A'} - ${customer.customer_name}`;
      }
    });
    
    // Add product labels for ALL selected products
    filters.product_ids.forEach(productId => {
      const product = availableProducts.find(p => p.id === productId);
      if (product) {
        filtersToApply.labels.products[productId] = product.name;
      }
    });
    
    // Add addon labels for ALL selected addons
    filters.addon_ids.forEach(addonId => {
      const addon = availableAddons.find(a => a.id === addonId);
      if (addon) {
        filtersToApply.labels.addons[addonId] = addon.name;
      }
    });
    
    onApply(filtersToApply);
  };

  // Cancel/Reset
  const handleCancel = () => {
    setFilters({
      customer_ids: [],
      product_ids: [],
      addon_ids: [],
      payment_status: '',
      appointment_status: ''
    });
    setSelectedCustomersData({});
    setShowVehicleCustomerPicker(false);
    setShowServicesPicker(false);
    setVehicleCustomerSearch('');
    setServiceSearchTerm('');
    setSearchPerformed(false);
    setSearching(false);
    onClose();
  };

  // Filter services based on search term
  const filteredServices = React.useMemo(() => {
    const allServices = [...availableProducts, ...availableAddons];
    if (!serviceSearchTerm.trim()) {
      return allServices;
    }
    return allServices.filter(service => 
      service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
      (service.category && service.category.toLowerCase().includes(serviceSearchTerm.toLowerCase()))
    );
  }, [availableProducts, availableAddons, serviceSearchTerm]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          m: { xs: 0, md: 2 },
          height: '100%',
          maxHeight: { xs: '100vh', md: '700px' },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        pt: 3,
        px: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.5rem' }}>
          Advanced Filters
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 4, 
        pb: 3,
        px: 3,
        flex: 1, 
        overflow: 'auto' 
      }}>
        <Stack spacing={4} sx={{ mt: 2 }}>
          {/* Customer Selection */}
          <Box>
            <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
              Customers
            </Typography>
            
            <Box sx={{ position: 'relative' }} ref={customerInputRef}>
              <OutlinedInput
                fullWidth
                placeholder="Select Customers"
                readOnly
                onClick={() => {
                  setShowVehicleCustomerPicker(true);
                  setTimeout(() => {
                    scrollToTop(customerInputRef);
                    // Auto-focus the search input
                    if (customerSearchInputRef.current) {
                      customerSearchInputRef.current.focus();
                    }
                  }, 100);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                sx={{
                  cursor: 'pointer',
                  '& .MuiOutlinedInput-input': {
                    cursor: 'pointer'
                  }
                }}
              />

              {/* Selected Customers Display */}
              {filters.customer_ids.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filters.customer_ids.map((customerId) => {
                    // Try to get customer from persistent data first, then from search results
                    const customer = selectedCustomersData[customerId] || garageCustomers.find(c => c.id === customerId);
                    if (!customer) return null;
                    
                    return (
                      <Chip
                        key={customerId}
                        label={`${customer.vehicle.registration_number} - ${customer.customer_name}`}
                        size="small"
                        onDelete={() => {
                          setFilters(prev => ({
                            ...prev,
                            customer_ids: prev.customer_ids.filter(id => id !== customerId)
                          }));
                          // Also remove from persistent data when deselected
                          setSelectedCustomersData(prev => {
                            const newData = { ...prev };
                            delete newData[customerId];
                            return newData;
                          });
                        }}
                        sx={{
                          backgroundColor: 'primary.50',
                          color: 'primary.main',
                          fontSize: '0.75rem',
                          height: 32,
                          '& .MuiChip-label': {
                            px: 1.5,
                            py: 0.5
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              {/* Customer Picker */}
              {showVehicleCustomerPicker && (
                <Card
                  ref={vehicleCustomerPickerRef}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    mt: 1,
                    maxHeight: 400,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. AB12 CDE or John Smith"
                      value={vehicleCustomerSearch}
                      inputRef={customerSearchInputRef}
                      onChange={(e) => {
                        setVehicleCustomerSearch(e.target.value);
                        if (searchPerformed) {
                          setSearchPerformed(false);
                          setGarageCustomers([]);
                          setFilteredGarageCustomers([]);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          searchGarageCustomers();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={searchGarageCustomers}
                              disabled={!vehicleCustomerSearch.trim()}
                              sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                              <SearchIcon sx={{ fontSize: 18 }} />
                            </Button>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 300 }}>
                    {searching ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={24} sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Searching...
                        </Typography>
                      </Box>
                    ) : filteredGarageCustomers.length > 0 ? (
                      filteredGarageCustomers.map((customer) => (
                        <Box
                          key={customer.id}
                          onClick={() => handleVehicleCustomerSelect(customer)}
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              transform: 'translateX(2px)'
                            },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: { xs: 1.5, sm: 2 },
                            flexDirection: 'column'
                          }}>
                            {/* Registration and MOT - Top Row */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              gap: { xs: 0.8, sm: 1.5 },
                              width: '100%'
                            }}>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, sm: 1.5 },
                                flexShrink: 0
                              }}>
                                <CarIcon sx={{ 
                                  color: 'primary.main', 
                                  fontSize: { xs: 20, sm: 24 },
                                  flexShrink: 0
                                }} />
                                <Box sx={{
                                  backgroundColor: '#FEDE00',
                                  border: '2px solid #000000',
                                  borderRadius: 1,
                                  px: { xs: 0.8, sm: 1.5 },
                                  py: { xs: 0.3, sm: 0.5 },
                                  minWidth: { xs: '60px', sm: '75px' },
                                  textAlign: 'center'
                                }}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{ 
                                      fontWeight: 900,
                                      fontSize: { xs: '0.65rem', sm: '0.85rem' },
                                      color: '#000000',
                                      letterSpacing: { xs: '0.2px', sm: '0.5px' },
                                      fontFamily: 'monospace',
                                      textTransform: 'uppercase',
                                      lineHeight: 1.2
                                    }}
                                  >
                                    {customer.vehicle.registration_number}
                                  </Typography>
                                </Box>
                              </Box>
                              {customer.vehicle.mot_expiry_date && (
                                <Chip
                                  label={`MOT expires on ${new Date(customer.vehicle.mot_expiry_date).toLocaleDateString('en-GB')}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    height: { xs: 22, sm: 24 },
                                    backgroundColor: 'transparent',
                                    ml: 'auto'
                                  }}
                                />
                              )}
                            </Box>

                            {/* Vehicle Details */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 0.5,
                              width: '100%'
                            }}>
                              {[customer.vehicle.make, customer.vehicle.model, customer.vehicle.year, customer.vehicle.colour]
                                .filter(Boolean).map((detail, index) => (
                                <Chip
                                  key={index}
                                  label={detail}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    backgroundColor: 'transparent',
                                    borderColor: 'divider',
                                    color: 'text.secondary'
                                  }}
                                />
                              ))}
                            </Box>

                            {/* Customer Info */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1.5, sm: 2 },
                              width: '100%'
                            }}>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <PersonIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                                {customer.customer_name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <PhoneIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                                {customer.mobile}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))
                    ) : searchPerformed && vehicleCustomerSearch.trim() ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No customers found
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Enter customer name or vehicle registration to search
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              )}
            </Box>
          </Box>

          {/* Services Selection */}
          <Box>
            <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
              Services
            </Typography>
            
            {availableProducts.length === 0 && availableAddons.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                bgcolor: 'warning.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.200',
                textAlign: 'center'
              }}>
                <Typography variant="body1" color="warning.dark" sx={{ mb: 1 }}>
                  No services available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please configure garage products and add-ons in garage settings to enable service selection.
                </Typography>
              </Box>
            ) : (
              <Box ref={servicesInputRef}>
                {/* Services Input Field */}
                <FormControl fullWidth>
                  <OutlinedInput
                    placeholder={(filters.product_ids.length > 0 || filters.addon_ids.length > 0) ? "" : "Select Services"}
                    readOnly
                    onClick={() => {
                      setShowServicesPicker(true);
                      setTimeout(() => {
                        scrollToTop(servicesInputRef);
                      }, 100);
                    }}
                    value=""
                    startAdornment={
                      (filters.product_ids.length > 0 || filters.addon_ids.length > 0) ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 0.5, 
                          mr: 1,
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'flex-start'
                        }}>
                          {/* Show selected products */}
                          {filters.product_ids.map((productId) => {
                            const product = availableProducts.find(p => p.id === productId);
                            if (!product) return null;
                            return (
                              <Chip
                                key={`product-${productId}`}
                                label={product.name}
                                size="small"
                                color="primary"
                                onDelete={(e) => {
                                  e.stopPropagation();
                                  setFilters(prev => ({
                                    ...prev,
                                    product_ids: prev.product_ids.filter(id => id !== productId)
                                  }));
                                }}
                                sx={{
                                  height: 28,
                                  fontSize: '0.75rem',
                                  flex: 1,
                                  minWidth: '80px',
                                  maxWidth: '200px',
                                  justifyContent: 'flex-start',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'left',
                                    paddingLeft: '8px',
                                    paddingRight: '4px'
                                  },
                                  '& .MuiChip-deleteIcon': {
                                    fontSize: '16px',
                                    marginLeft: 'auto'
                                  }
                                }}
                              />
                            );
                          })}
                          {/* Show selected addons */}
                          {filters.addon_ids.map((addonId) => {
                            const addon = availableAddons.find(a => a.id === addonId);
                            if (!addon) return null;
                            return (
                              <Chip
                                key={`addon-${addonId}`}
                                label={addon.name}
                                size="small"
                                color="success"
                                onDelete={(e) => {
                                  e.stopPropagation();
                                  setFilters(prev => ({
                                    ...prev,
                                    addon_ids: prev.addon_ids.filter(id => id !== addonId)
                                  }));
                                }}
                                sx={{
                                  height: 28,
                                  fontSize: '0.75rem',
                                  flex: 1,
                                  minWidth: '80px',
                                  maxWidth: '200px',
                                  justifyContent: 'flex-start',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'left',
                                    paddingLeft: '8px',
                                    paddingRight: '4px'
                                  },
                                  '& .MuiChip-deleteIcon': {
                                    fontSize: '16px',
                                    marginLeft: 'auto'
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      ) : null
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {(filters.product_ids.length > 0 || filters.addon_ids.length > 0) && (
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilters(prev => ({ ...prev, product_ids: [], addon_ids: [] }));
                                setServiceSearchTerm('');
                              }}
                              sx={{ 
                                textTransform: 'none',
                                minWidth: 'auto',
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                p: 0.5,
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              Clear All
                            </Button>
                          )}
                          <ExpandMoreIcon sx={{ 
                            transform: showServicesPicker ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease'
                          }} />
                        </Box>
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      minHeight: (filters.product_ids.length > 0 || filters.addon_ids.length > 0) ? 'auto' : '56px',
                      alignItems: 'center',
                      py: (filters.product_ids.length > 0 || filters.addon_ids.length > 0) ? 1 : 1.5,
                      '& .MuiOutlinedInput-input': {
                        textAlign: 'left'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: showServicesPicker ? 'primary.main' : 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }}
                  />
                </FormControl>

                {/* Services Picker Widget */}
                {showServicesPicker && (
                  <Box 
                    ref={servicesPickerRef}
                    sx={{ 
                      mt: 1, 
                      p: 0,
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 3,
                      position: 'relative',
                      zIndex: 1000
                    }}
                  >
                    {/* Search Bar */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <TextField
                        fullWidth
                        placeholder="Enter to Search"
                        value={serviceSearchTerm}
                        inputRef={servicesSearchInputRef}
                        onChange={(e) => setServiceSearchTerm(e.target.value)}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5
                          }
                        }}
                      />
                    </Box>

                    {/* Services List */}
                    <Box sx={{ 
                      maxHeight: 300,
                      overflowY: 'auto'
                    }}>
                      {filteredServices.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {serviceSearchTerm ? `No services found matching "${serviceSearchTerm}"` : 'No services available'}
                          </Typography>
                        </Box>
                      ) : (
                        filteredServices.map((service, index) => {
                          const isProduct = availableProducts.some(p => p.id === service.id);
                          const isSelected = isProduct 
                            ? filters.product_ids.includes(service.id)
                            : filters.addon_ids.includes(service.id);
                          const toggleHandler = isProduct ? handleProductToggle : handleAddonToggle;
                          
                          return (
                            <Box key={service.id}>
                              <Box
                                sx={{
                                  p: 2.5,
                                  cursor: 'pointer',
                                  bgcolor: isSelected ? (isProduct ? 'primary.50' : 'success.50') : 'transparent',
                                  '&:hover': {
                                    bgcolor: isSelected 
                                      ? (isProduct ? 'primary.100' : 'success.100') 
                                      : 'action.hover'
                                  },
                                  transition: 'background-color 0.2s ease'
                                }}
                                onClick={() => toggleHandler(service)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {/* Icon/Avatar */}
                                  <Box
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 2,
                                      bgcolor: isSelected 
                                        ? (isProduct ? 'primary.main' : 'success.main')
                                        : (isProduct ? 'primary.100' : 'success.100'),
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      color: isSelected 
                                        ? (isProduct ? 'primary.contrastText' : 'success.contrastText')
                                        : (isProduct ? 'primary.main' : 'success.main'),
                                      fontWeight: 'bold',
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    {isProduct 
                                      ? (service.category ? service.category.charAt(0) : service.name.charAt(0))
                                      : '+'
                                    }
                                  </Box>
                                  
                                  {/* Service Details */}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                                      {service.name}
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: 1.5, 
                                      flexWrap: 'wrap'
                                    }}>
                                      <Typography 
                                        variant="caption" 
                                        color={service.is_free ? 'success.main' : 'primary.main'} 
                                        fontWeight="600"
                                      >
                                        {service.is_free ? 'Free' : `£${service.custom_price || service.default_price}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {service.custom_duration || service.duration_minutes || 0} mins
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  {/* Checkbox */}
                                  <Box onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => toggleHandler(service)}
                                      size="small"
                                      sx={{
                                        color: isProduct ? 'primary.main' : 'success.main',
                                        '&.Mui-checked': {
                                          color: isProduct ? 'primary.main' : 'success.main'
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                              {index < filteredServices.length - 1 && (
                                <Divider sx={{ mx: 2.5 }} />
                              )}
                            </Box>
                          );
                        })
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ 
                      p: 2, 
                      borderTop: '1px solid', 
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        {filters.product_ids.length + filters.addon_ids.length} selected
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setFilters(prev => ({ ...prev, product_ids: [], addon_ids: [] }));
                            setServiceSearchTerm('');
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear All
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setShowServicesPicker(false);
                            setServiceSearchTerm('');
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Done
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Payment Status - Single Selection */}
          <Box ref={paymentStatusInputRef}>
            <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
              Payment Status
            </Typography>
            <FormControl fullWidth>
              <Select
                value={filters.payment_status}
                onChange={handlePaymentStatusChange}
                onOpen={() => {
                  setTimeout(() => scrollToTop(paymentStatusInputRef), 100);
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return 'All';
                  }
                  const selectedOption = paymentStatusOptions.find(option => option.value === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: selectedOption?.color || 'text.secondary'
                        }}
                      />
                      {selectedOption?.label || selected}
                    </Box>
                  );
                }}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }}
              >
                <MenuItem value="">
                  All
                </MenuItem>
                {paymentStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: option.color
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Appointment Status - Single Selection */}
          <Box ref={appointmentStatusInputRef}>
            <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
              Appointment Status
            </Typography>
            <FormControl fullWidth>
              <Select
                value={filters.appointment_status}
                onChange={handleAppointmentStatusChange}
                onOpen={() => {
                  setTimeout(() => scrollToTop(appointmentStatusInputRef), 100);
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return 'All';
                  }
                  const selectedOption = appointmentStatusOptions.find(option => option.value === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: selectedOption?.color || 'text.secondary'
                        }}
                      />
                      {selectedOption?.label || selected}
                    </Box>
                  );
                }}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }}
              >
                <MenuItem value="">
                  All
                </MenuItem>
                {appointmentStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: option.color
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1
      }}>
        <Button 
          onClick={handleCancel} 
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleApply}
          variant="contained"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100
          }}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;